import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CheckCircle2, LoaderCircle, RotateCcw, Undo2 } from 'lucide-react';
import { NOTICE_SHOWN_MS } from './WorkspaceNotice';
import {
  clearedNewsParts,
  clearedNewsText,
  clearedNewsUndoable,
  type ClearedNews,
} from './cleared-news';

/** How long the line takes to fade at the end of its time. */
const FADE_MS = 600;

let measuring: CanvasRenderingContext2D | null = null;
/** Width of a sentence in the line's own font, without laying it out. */
function textWidth(text: string, node: HTMLElement): number {
  measuring ??= document.createElement('canvas').getContext('2d');
  if (!measuring) return 0;
  const style = getComputedStyle(node);
  measuring.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  return measuring.measureText(text).width;
}

/**
 * The one line, under the documents list's counts, that says learning just took more
 * documents out of Needs review, or sent released ones back to it (cleared-news.ts). It goes on its own after the usual
 * notice time; nothing to dismiss. It waits while the pointer or focus is on it, so
 * its quiet Undo never disappears from under a click. When the whole sentence does not
 * fit on one line it says the short form ("Cleared 3 more Invoices") rather than wrap
 * or cut the news off.
 */
export function ClearedNewsLine({
  news,
  onUndo,
  undoing,
  onExpire,
}: {
  news: ClearedNews;
  /** Puts the engine's releases back in review; offered only when it covers every document named. */
  onUndo?: () => void;
  undoing?: boolean;
  onExpire: () => void;
}) {
  const [held, setHeld] = useState(false);
  const [fading, setFading] = useState(false);
  const expire = useRef(onExpire);
  expire.current = onExpire;
  useEffect(() => {
    setFading(false);
    if (held || undoing) return;
    const fade = window.setTimeout(() => setFading(true), NOTICE_SHOWN_MS - FADE_MS);
    const gone = window.setTimeout(() => expire.current(), NOTICE_SHOWN_MS);
    return () => {
      window.clearTimeout(fade);
      window.clearTimeout(gone);
    };
  }, [news.serial, held, undoing]);
  const text = clearedNewsText(news);
  const { lead, short, rest } = clearedNewsParts(news);
  const undoable = Boolean(onUndo) && clearedNewsUndoable(news);
  const lineRef = useRef<HTMLParagraphElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [compact, setCompact] = useState(false);
  useLayoutEffect(() => {
    const line = lineRef.current,
      words = textRef.current;
    if (!line || !words || !lead) {
      setCompact(false);
      return;
    }
    const measure = () => {
      const gap = parseFloat(getComputedStyle(line).columnGap) || 0;
      const others = [...line.children].filter((child) => child !== words);
      const room =
        line.clientWidth -
        others.reduce((sum, child) => sum + child.getBoundingClientRect().width, 0) -
        gap * others.length;
      setCompact(textWidth(text, words) > room + 0.5);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(line);
    return () => observer.disconnect();
  }, [text, lead, undoable, undoing]);
  const reopened = news.reopened !== null;
  const back = news.direction === 'back';
  return (
    <p
      ref={lineRef}
      className={`spine-cleared-news${back ? ' back' : ''}${reopened ? ' reopened' : ''}${fading ? ' fading' : ''}`}
      role="status"
      aria-live="polite"
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHeld(false);
      }}
    >
      {back ? (
        <RotateCcw size={12} aria-hidden="true" />
      ) : reopened ? (
        <Undo2 size={15} aria-hidden="true" />
      ) : (
        <CheckCircle2 size={15} aria-hidden="true" />
      )}
      <span ref={textRef} className="spine-cleared-text" title={text}>
        {lead ? `${compact ? short : lead} ${rest}` : rest}
      </span>
      {undoable && (
        <button
          type="button"
          className="spine-cleared-undo"
          onClick={onUndo}
          disabled={undoing}
          aria-label={`Undo: put ${news.documents.length === 1 ? 'it' : 'them'} back in Needs review`}
        >
          {undoing && <LoaderCircle className="spin" size={12} aria-hidden="true" />}Undo
        </button>
      )}
    </p>
  );
}
